import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause } from 'lucide-react';
import { cn } from '../../lib/utils';

interface AudioPlayerProps {
  src: string;
  duration?: number;
}

export function AudioPlayer({ src, duration: initialDuration }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    audioRef.current.currentTime = percentage * duration;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-bg-tertiary">
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Play/Pause button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={togglePlay}
        className="w-10 h-10 flex-shrink-0 rounded-full bg-accent text-white flex items-center justify-center shadow-lg shadow-accent/25"
      >
        {isPlaying ? (
          <Pause className="w-4 h-4" />
        ) : (
          <Play className="w-4 h-4 ml-0.5" />
        )}
      </motion.button>

      {/* Waveform/Progress */}
      <div className="flex-1 space-y-1">
        <div
          className="relative h-8 cursor-pointer"
          onClick={handleSeek}
        >
          {/* Waveform bars (decorative) */}
          <div className="absolute inset-0 flex items-center gap-0.5">
            {Array.from({ length: 40 }).map((_, i) => {
              const barProgress = (i / 40) * 100;
              const isPlayed = barProgress <= progress;
              const height = Math.sin((i / 40) * Math.PI * 3) * 12 + 8;

              return (
                <motion.div
                  key={i}
                  className={cn(
                    'w-1 rounded-full transition-colors duration-150',
                    isPlayed ? 'bg-accent' : 'bg-border'
                  )}
                  animate={
                    isPlaying && isPlayed
                      ? {
                          height: [height, height + 4, height],
                        }
                      : { height }
                  }
                  transition={{
                    duration: 0.3,
                    repeat: isPlaying && isPlayed ? Infinity : 0,
                    delay: i * 0.02,
                  }}
                  style={{ height }}
                />
              );
            })}
          </div>
        </div>

        {/* Time */}
        <div className="flex justify-between text-xs text-text-muted">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}
